import React, { useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";

import { Field, FieldLabel } from "../../../../components/FormPanel";
import { buildSelectStyles } from "../../../../components/Input";
import {
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_MS,
  SEARCH_TOO_SHORT_MESSAGE,
} from "../../../../components/Input/InstrumentSearch";
import { SectionRetryButton } from "../../../../components/SectionRetryButton";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import {
  CatalogueSearchResult,
  useSearchCatalogInstrumentsQuery,
} from "../../../../services/instruments";
import {
  EditorError,
  EditorRetryRow,
  OptionMeta,
  OptionSymbol,
} from "./styles";
import { CatalogInstrumentOption, ExistingInstrumentFieldProps } from "./types";

const INPUT_ID = "instrument-editor-existing";

const toOption = (
  instrument: CatalogueSearchResult,
): CatalogInstrumentOption => ({
  value: String(instrument.id),
  label: `${instrument.symbol} · ${instrument.exchange} — ${instrument.name}`,
  instrument,
});

export const ExistingInstrumentField: React.FC<
  ExistingInstrumentFieldProps
> = ({ control, disabled, onPick }) => {
  const [typedTerm, setTypedTerm] = useState("");
  const debouncedTerm = useDebouncedValue(typedTerm.trim(), SEARCH_DEBOUNCE_MS);

  const { currentData, isFetching, isError, refetch } =
    useSearchCatalogInstrumentsQuery(debouncedTerm, {
      skip: debouncedTerm.length < MIN_SEARCH_LENGTH,
    });
  const options = useMemo(
    () => (currentData ?? []).map(toOption),
    [currentData],
  );
  const emptyMessage =
    debouncedTerm.length < MIN_SEARCH_LENGTH
      ? SEARCH_TOO_SHORT_MESSAGE
      : "No instruments found";

  return (
    <Field>
      <FieldLabel htmlFor={INPUT_ID}>Catalog instrument</FieldLabel>
      <Controller
        name="instrument"
        control={control}
        rules={{ required: "Choose a catalog instrument" }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Select<CatalogInstrumentOption>
              inputId={INPUT_ID}
              aria-invalid={Boolean(error) || undefined}
              isDisabled={disabled}
              isLoading={isFetching}
              value={value}
              options={options}
              onChange={(option) => {
                onChange(option);
                onPick(option?.instrument ?? null);
              }}
              onInputChange={(next, meta) => {
                if (meta.action === "input-change") {
                  setTypedTerm(next);
                }
              }}
              filterOption={null}
              placeholder="Search the catalog, e.g. VWCE"
              noOptionsMessage={() => emptyMessage}
              menuPortalTarget={document.body}
              styles={buildSelectStyles<CatalogInstrumentOption>({
                hasError: Boolean(error),
                disabled,
              })}
              formatOptionLabel={({ instrument }) => (
                <>
                  <OptionSymbol>{instrument.symbol}</OptionSymbol>
                  <OptionMeta>
                    {instrument.exchange} · {instrument.name}
                  </OptionMeta>
                </>
              )}
            />
            {error && <EditorError role="alert">{error.message}</EditorError>}
          </>
        )}
      />
      {isError && (
        <EditorRetryRow>
          <EditorError role="alert">The catalog search failed.</EditorError>
          <SectionRetryButton onClick={() => void refetch()} />
        </EditorRetryRow>
      )}
    </Field>
  );
};
